import { useMemo, useState } from 'react';

import styled from 'styled-components';

import { AuditLogFragmentFragment, useGetAuditLogsQuery } from 'data/__generated__';

import { getExtendedFormattedDatetime } from 'helpers/dateTimes';

import { Loader } from 'components/Loader';

type Props = {
    userId: string;
};

type Patch = AuditLogFragmentFragment['patches'][number];

const formatPatchValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

const renderPatchDescription = (patch: Patch): string => {
    switch (patch.__typename) {
        case 'ReplacePatch':
            return `${patch.path} : ${formatPatchValue(patch.oldValue)} → ${formatPatchValue(patch.value)}`;
        case 'AddPatch':
            return `+ ${patch.path} : ${formatPatchValue(patch.value)}`;
        case 'RemovePatch':
            return `− ${patch.path} (était : ${formatPatchValue(patch.oldValue)})`;
        case 'MovePatch':
        case 'CopyPatch':
            return `${patch.path} ← ${patch.from}`;
        case 'TestPatch':
            return `test ${patch.path} = ${formatPatchValue(patch.value)}`;
        default:
            return '—';
    }
};

export const AuditLogSection = ({ userId }: Props) => {
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const { data, loading, error } = useGetAuditLogsQuery({
        variables: { collectionDocumentId: userId },
    });

    const sortedLogs = useMemo(() => {
        const logs = data?.auditLogs ?? [];
        return [...logs].sort((a, b) => b.createdAt - a.createdAt);
    }, [data?.auditLogs]);

    if (loading) {
        return <Loader />;
    }

    if (error) {
        return <Empty>Erreur de chargement de l'historique.</Empty>;
    }

    if (sortedLogs.length === 0) {
        return <Empty>Aucune modification admin enregistrée.</Empty>;
    }

    return (
        <List>
            {sortedLogs.map((log) => {
                const isExpanded = expandedId === log._id;
                const patchCount = log.patches.length;

                return (
                    <LogItem key={log._id}>
                        <LogHeader onClick={() => setExpandedId(isExpanded ? null : log._id)}>
                            <Date>{getExtendedFormattedDatetime(log.createdAt)}</Date>
                            <Author>{log.createdByName || log.createdBy}</Author>
                            <Count>
                                {patchCount} modification{patchCount > 1 ? 's' : ''}
                            </Count>
                            <ToggleIcon>{isExpanded ? '−' : '+'}</ToggleIcon>
                        </LogHeader>
                        {isExpanded ? (
                            <PatchList>
                                {log.patches.map((patch, idx) => (
                                    <PatchRow key={`${log._id}-${idx}`}>{renderPatchDescription(patch)}</PatchRow>
                                ))}
                            </PatchList>
                        ) : null}
                    </LogItem>
                );
            })}
        </List>
    );
};

const Empty = styled.div`
    color: ${({ theme }) => theme.textColor};
    opacity: 0.6;
    font-size: 13px;
    padding: 8px 0;
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 480px;
    overflow-y: auto;
`;

const LogItem = styled.div`
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: var(--radius-md);
`;

const LogHeader = styled.div`
    display: grid;
    grid-template-columns: 200px 1fr auto 24px;
    align-items: center;
    gap: 12px;
    padding: 10px 14px;
    cursor: pointer;
    color: ${({ theme }) => theme.textColor};

    &:hover {
        background-color: ${({ theme }) => theme.backgroundColor};
    }
`;

const Date = styled.span`
    font-size: 12px;
    opacity: 0.75;
    font-variant-numeric: tabular-nums;
`;

const Author = styled.span`
    font-size: 13px;
    font-weight: 500;
`;

const Count = styled.span`
    font-size: 12px;
    opacity: 0.7;
`;

const ToggleIcon = styled.span`
    font-size: 18px;
    text-align: center;
    color: ${({ theme }) => theme.ctaPrimaryColor};
`;

const PatchList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 14px 14px;
    border-top: 1px solid ${({ theme }) => theme.lightBorderColor};
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
`;

const PatchRow = styled.div`
    color: ${({ theme }) => theme.textColor};
    word-break: break-word;
`;
